import {
  ChangeDetectorRef,
  Component,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { SelectItem } from '@openng/optimus-ui/api';
import { firstValueFrom, forkJoin, Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { MOC_EVENT_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import { VIEW_HISTORICAL_RECORD } from 'src/app/constants/action-constants';
import {
  ACKNOWLEDGE_MOC,
  DELETE_MOC_EVENT,
  EVENT_STATUS_CLOSED,
  EVENT_STATUS_OPEN,
  EVENT_STATUS_UPCOMING,
  EVENT_TYPE_PERMANENT,
  EVENT_TYPE_SCHEDULED,
  MANUALLY_CLOSE_MOC,
  MOC_EVENT_TAB_PREFIX,
} from 'src/app/constants/crm-constants';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import {
  getDirtyValues,
  isValidUrl,
} from 'src/app/core/functions/common-functions';
import { Column } from 'src/app/model/common/column';
import { DirtyStatus } from 'src/app/model/common/dirty-status';
import { MOCEvent } from 'src/app/model/crm/moc-event';
import { MOCEventAckInput } from 'src/app/model/crm/moc-event-ack-input';
import { MOCEventAcknowledgement } from 'src/app/model/crm/moc-event-acknowledgement';
import { MOCEventEntry } from 'src/app/model/crm/moc-event-entry';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { AuthService } from 'src/app/services/auth.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { MocEventApiService } from 'src/app/services/api/moc-event-api.service';
import { MocEventBuilderService } from 'src/app/services/builder/moc-event-builder.service';
import { MocEventTableService } from 'src/app/services/table/moc-event-table.service';
import { ShiftTurnoverApiService } from 'src/app/services/api/shift-turnover-api.service';
import { getMOCEventAcknowledgementColumns } from '../../definitions/moc-event-acknow-table-definition';
import {
  isValidMocEventInput,
  verifyMOCEventCategoryStillValid,
} from '../../functions/moc-event-functions';
import { isNullOrUndefined, sortByProperty } from 'src/app/utils/utils';
import { Contact } from 'src/app/model/contacts/contact';
import { CLAIMS } from 'src/app/constants/auth-constants';
import { Select, SelectChangeEvent } from '@openng/optimus-ui/select';
import { AOR } from 'src/app/model/locations/aor';
import { BlockUI } from '@openng/optimus-ui/blockui';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { InputText } from '@openng/optimus-ui/inputtext';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { Panel } from '@openng/optimus-ui/panel';
import { LogEntryComponent } from '../../../core/components/log-entry.component';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Dialog } from '@openng/optimus-ui/dialog';
import { Editor } from '@openng/optimus-ui/editor';
import { TranslatePipe } from '@ngx-translate/core';
import { BlockableDiv } from 'src/app/common/components/blockable-div/blockable-div.component';
import { Textarea } from '@openng/optimus-ui/textarea';
import { DialogService } from '@openng/optimus-ui/dynamicdialog';
import { HistoryVersionDialogComponent } from 'src/app/core/containers/history-version-dialog/history-version-dialog.component';

@Component({
  selector: 'app-moc-event-details-container',
  templateUrl: './moc-event-details-container.component.html',
  styleUrls: ['./moc-event-details-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    BlockUI,
    ProgressSpinnerComponent,
    FormsModule,
    ReactiveFormsModule,
    Select,
    ButtonDirective,
    InputText,
    MultiSelect,
    DatePicker,
    Panel,
    LogEntryComponent,
    GeneralGridComponent,
    Dialog,
    Editor,
    TranslatePipe,
    BlockableDiv,
    Textarea,
  ],
})
export class MocEventDetailsContainerComponent
  extends DetailsContainer
  implements OnInit
{
  protected _fb = inject(UntypedFormBuilder);
  protected _cdRef = inject(ChangeDetectorRef);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _mocApi = inject(MocEventApiService);
  protected _mocEventTableService = inject(MocEventTableService);
  protected _mocBuilder = inject(MocEventBuilderService);
  protected _shiftApi = inject(ShiftTurnoverApiService);
  protected _auth = inject(AuthService);
  protected _authApi = inject(AuthApiService);
  protected _dialogService = inject(DialogService);

  mocEvent: MOCEvent | null = null;
  eventTypes: SelectItem[] = [];
  eventCategories: SelectItem[] = [];
  eventEntries: MOCEventEntry[] = [];
  districts: SelectItem[] = [];
  shiftGroups: SelectItem[] = [];
  gridHeader = 'Acknowledgements';
  acknowledgements: MOCEventAcknowledgement[] = [];
  ackColumns: Column[] = [];
  actions: SelectItem[] = [];
  selectedAction: SelectItem | null = null;
  ackForm?: UntypedFormGroup;
  selectedAck?: MOCEventAcknowledgement;

  canAcknowledge = false;
  canCreate = false;
  canDelete = false;
  isEditting = false;
  saving = false;
  showEntryDialog = false;
  addEntryForm?: UntypedFormGroup;
  activeTabChangedSub?: Subscription;
  historicalMocEvent?: MOCEvent;
  isHistoryRecord = false;
  defaultStartDate: Date = new Date();
  defaultEndDate: Date = new Date();

  constructor() {
    super();

    this.form = this._mocBuilder.buildMOCEventForm(this._fb);
  }

  ngOnInit() {
    this.ackForm = this._mocBuilder.buildMOCAcknowledgementForm(this._fb);
    this.addEntryForm = this._fb.group({
      entry: [null, Validators.required],
      id: null,
    });

    this.setupTabClosingSubscription();
    this.setupActiveTabChangedSubscription();
    this.initialize();

    this.setDefaultDates();
  }

  initialize(): void {
    this.loading = true;
    const id = this.id;
    if (!isNullOrUndefined(id)) {
      this._mocApi.getMOCEventById(id).subscribe({
        next: (mocEvent) => {
          if (!isNullOrUndefined(mocEvent)) {
            this.mocEvent = mocEvent;
            this.determineEventStatus(this.mocEvent);
            this.acknowledgements = [...this.mocEvent.MOCEventAcknowledgements];
            this.eventEntries = [
              ...this.mocEvent.MOCEventEntries.sort((a, b) =>
                a.time && b.time ? b.time.getTime() - a.time.getTime() : 0
              ),
            ];
          }
        },
        error: (error) => {
          this.isEditting = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENT_BY_ID',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.isEditting = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'CRM.MESSAGES.SUCCESS.GET_MOC_EVENT_BY_ID',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          });
          this.loadData();
        },
      });
    } else {
      this.mocEvent =
        this._mocEventTableService.getSelected() as unknown as MOCEvent;
      if (isNullOrUndefined(this.mocEvent)) {
        this._logAndMessage.errorLogOnly('MOC Event was null');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.loadData();
      this.eventEntries = [
        ...this.mocEvent!.MOCEventEntries.sort((a, b) =>
          a.time && b.time ? b.time.getTime() - a.time.getTime() : 0
        ),
      ];

      if (this.mocEvent.id === null && this.mocEvent.title === null) {
        // We know that we are starting with a null moc, and should be in edit mode because this is a new moc
        this.isEditting = true;
      }
    }
    if (!this.isEditting) {
      this.form?.disable();
    }

    this.canAcknowledge = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.ACKNOWLEDGE_MOC_EVENTS,
    ]);
    this.canCreate = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.CREATE_MOC_EVENTS,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.MOC_EVENTS.DELETE_MOC_EVENTS,
    ]);
  }

  async loadData(): Promise<void> {
    const labels = await firstValueFrom(
      forkJoin({
        new: this._translateService.get('CRM.LABEL.NEW_MOC_EVENT'),
        regularPrefix: this._translateService.get('CRM.LABEL.MOC_EVENT'),
      })
    );
    this.loadDropDowns();
    const tab = this.mocEvent?.id
      ? this._tabService.getTabById(this.mocEvent.id)
      : this._tabService.getActiveTab();
    // Historical tabs are opened with a " - V{historyId}" label; detect it before relabeling.
    const historySuffix = ' - V' + this.mocEvent?.historyId;
    this.isHistoryRecord =
      !!this.mocEvent?.id && !!tab?.header.endsWith(historySuffix);

    if (!this.mocEvent?.id) {
      this.screenName = labels.new;
    } else {
      this.screenName = labels.regularPrefix + ': ' + this.mocEvent!.title;
      if (this.isHistoryRecord) {
        this.screenName += historySuffix;
      }
    }

    if (this.mocEvent?.id) {
      this._tabService.updateTabLabel(tab!, this.screenName);
    } else {
      this._tabService.updateActiveTabLabel(this.screenName);
    }

    this.ackColumns = getMOCEventAcknowledgementColumns();
    this.isEditting = this.mocEvent && this.mocEvent.id ? false : true;
    this.loading = false;
    if (this.mocEvent && this.mocEvent.id) {
      this.determineEventStatus(this.mocEvent);
    }
    this.updateActionList();
    this.updateForm();
  }

  /**
   * Populates available actions for the MOC Event screen.
   */
  updateActionList(): void {
    const selectAction$ = this._translateService.get('COMMON.LABEL.CHOOSE');
    const acknowledge$ = this._translateService.get(
      'CRM.LABEL.ACKNOWLEDGE_MOC'
    );
    const viewHistory$ = this._translateService.get(
      'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
    );
    const manClose$ = this._translateService.get('CRM.LABEL.MANUALLY_CLOSE');
    const delete$ = this._translateService.get('COMMON.LABEL.BUTTONS.DELETE');

    let select,
      acknowledge,
      history,
      deleteMoc,
      manClose = null;
    forkJoin([selectAction$, acknowledge$, viewHistory$, manClose$, delete$])
      .pipe(take(1))
      .subscribe((messages) => {
        select = messages[0];
        acknowledge = messages[1];
        history = messages[2];
        manClose = messages[3];
        deleteMoc = messages[4];

        this.actions = [{ label: select, value: null }];

        if (!this.isHistoryRecord) {
          if (this.canAcknowledge) {
            this.actions = [
              ...this.actions,
              { label: acknowledge, value: ACKNOWLEDGE_MOC },
            ];
          }

          if (
            this.canCreate &&
            this.mocEvent &&
            (this.mocEvent.endDt < new Date() ||
              !this.mocEvent.endDt ||
              this.mocEvent.endDt > new Date())
          ) {
            this.actions = [
              ...this.actions,
              { label: manClose, value: MANUALLY_CLOSE_MOC },
            ];
          }

          if (
            this.canDelete &&
            this._authApi.doesUserHaveAllClaimsFromList([
              CLAIMS.CONTROL_ROOM.MOC_EVENTS.DELETE_MOC_EVENTS,
            ]) &&
            this.mocEvent &&
            this.mocEvent.eventStatus !== EVENT_STATUS_CLOSED
          ) {
            this.actions = [
              ...this.actions,
              { label: deleteMoc, value: DELETE_MOC_EVENT },
            ];
          }
        }

        this.actions = [
          ...this.actions,
          { label: history, value: VIEW_HISTORICAL_RECORD },
        ];

        this.actions = [...this.actions];
      });
  }

  /**
   * Method that determines what each action
   * item from the dropdown does.
   * @param $event
   * @param object
   */
  actionEvent($event: SelectChangeEvent, object: Select): void {
    this.selectedAction = null;
    if ($event.value) {
      if ($event.value === ACKNOWLEDGE_MOC) {
        this.acknowledgeMOCEvent();
      } else if ($event.value === VIEW_HISTORICAL_RECORD) {
        this.viewHistoricalRecord();
      } else if ($event.value === MANUALLY_CLOSE_MOC) {
        this.manuallyCloseMocEvent();
      } else if ($event.value === DELETE_MOC_EVENT) {
        this.deleteMOCEvent();
      }

      if (!isNullOrUndefined(object)) {
        object.clear();
      }
    }
    this._cdRef.markForCheck();
  }

  /**
   * Make the form editable for changes.
   */
  setEditting(): void {
    this.isEditting = true;
    this.form?.enable();
    this.form?.controls['createdBy'].disable();
    this.form?.controls['createdAt'].disable();
    this.form?.controls['eventStatus'].disable();
    if (
      this.mocEvent &&
      this.mocEvent.startDt < new Date() &&
      !this.canCreate
    ) {
      this.form?.controls['startDt'].disable();
    }
    if (
      this.mocEvent &&
      this.mocEvent.MOCEventType &&
      this.mocEvent.MOCEventType.name === EVENT_TYPE_PERMANENT &&
      !this.canCreate
    ) {
      this.form?.controls['endDt'].disable();
    }
  }

  /**
   * Opens document link in text box
   */
  OpenLink(): void {
    const url = this.form?.controls['document'].value;
    if (isValidUrl(url)) {
      window.open(url, '_blank');
    } else {
      this._logAndMessage.warn(
        'Warning: Document field is either empty or contains an invalid URL'
      );
    }
  }

  /**
   * Saves an MOC event.
   */
  saveMocEvent(): void {
    if (!this.canCreate) {
      return;
    }

    if (this.form && this.form.valid) {
      this.saving = true;
      if (!this.mocEvent?.id) {
        this.createMocEvent();
      } else {
        this.versionMocEvent();
      }
    }
  }

  /**
   * Method to determine the status of the MOC Event.
   * Events are open if the current date falls between
   * the start and end date, upcoming if the start date is in the
   * future, or closed if the end date is in the past.
   * @param data
   */
  determineEventStatus(data: any): void {
    const today = new Date();
    if (!isNullOrUndefined(data)) {
      const eventType = this.eventTypes.find(
        (et) => data.MOCEventType && et.value === data.MOCEventType.id
      );
      if (!isNullOrUndefined(eventType)) {
        if (new Date(data.startDt) > today) {
          this.form?.controls['eventStatus'].patchValue(EVENT_STATUS_UPCOMING, {
            emitEvent: true,
          });
          this.mocEvent!.eventStatus = EVENT_STATUS_UPCOMING;
        } else if (new Date(data.endDt) < today) {
          this.form?.controls['eventStatus'].patchValue(EVENT_STATUS_CLOSED, {
            emitEvent: true,
          });
          this.mocEvent!.eventStatus = EVENT_STATUS_CLOSED;
        } else {
          this.form?.controls['eventStatus'].patchValue(EVENT_STATUS_OPEN, {
            emitEvent: true,
          });
          this.mocEvent!.eventStatus = EVENT_STATUS_OPEN;
        }
      }
    }
  }

  /**
   * Create an MOC Event.
   * @param data
   */
  createMocEvent(): void {
    const event = this.form?.getRawValue();
    const gasControlGroup = this.shiftGroups.find(
      (sg) => sg.label === 'Gas Control Room'
    );
    const isPermanent = this.eventTypes.find(
      (et) => et.label === EVENT_TYPE_PERMANENT && et.value === event.eventType
    );
    const mocEventInput = this._mocBuilder.buildMOCEventNewInput(
      event,
      isPermanent!,
      gasControlGroup!.value
    );
    const isValidInput = isValidMocEventInput(mocEventInput);
    if (!isNullOrUndefined(isValidInput)) {
      this.saving = false;
      this._logAndMessage.translateToErrorAlert({
        bodyKey: isValidInput,
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
      return;
    }
    this._mocApi.createMOCEvent(mocEventInput).subscribe({
      next: (moc) => {
        this.mocEvent = moc;
        this.id = moc.id;
      },
      error: (error) => {
        this.isEditting = false;
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: 'CRM.MESSAGES.ERROR.CREATE_MOC_EVENT',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.isEditting = false;
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: 'CRM.MESSAGES.SUCCESS.CREATE_MOC_EVENT',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.determineEventStatus(this.mocEvent);
        this.updateForm();
        this.form?.disable();
        this.form?.markAsPristine();
        this.screenName = MOC_EVENT_TAB_PREFIX + this.mocEvent?.title;
        const oldTab = this._tabService.getActiveTab();
        const tab = this._tabService.buildNewTab(
          MOC_EVENT_DETAILS_CONTAINER,
          true,
          undefined,
          this.mocEvent!.id
        );
        this._tabService.replaceTab(oldTab, tab);
      },
    });
  }

  /**
   * Will create a new version of the MOC Event with an
   * increased history ID.
   */
  versionMocEvent(manualClose = false): void {
    const event = this.form?.getRawValue();
    const gasControlGroup = this.shiftGroups.find(
      (sg) => sg.label === 'Gas Control Room'
    );
    const isPermanent = this.eventTypes.find(
      (et) =>
        et.label === EVENT_TYPE_PERMANENT &&
        et.value === event.eventType &&
        !this._authApi.doesUserHaveAllClaimsFromList([
          'EditPermanentMOCEndDates',
        ])
    ); // Admin's should be able to change the end date, even for permenant records.
    const mocEventInput = this._mocBuilder.buildMOCEventVersionInput(
      event,
      isPermanent!,
      gasControlGroup!.value,
      this.mocEvent!,
      manualClose
    );
    const isValidInput = isValidMocEventInput(mocEventInput);
    if (!isNullOrUndefined(isValidInput)) {
      this.saving = false;
      this._logAndMessage.translateToErrorAlert({
        bodyKey: isValidInput,
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
      return;
    }
    this._mocApi.createMOCEvent(mocEventInput).subscribe({
      next: (moc) => {
        this.mocEvent = moc;
        this.id = moc.id;
      },
      error: (error) => {
        this.isEditting = false;
        this.saving = false;
        this._logAndMessage.errorLogOnly(error);
        this._logAndMessage.translateToErrorAlert({
          bodyKey: manualClose
            ? 'CRM.MESSAGES.ERROR.MANUALLY_CLOSE'
            : 'CRM.MESSAGES.ERROR.UPDATE_MOC_EVENT',
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
        });
      },
      complete: () => {
        this.isEditting = false;
        this.saving = false;
        this._logAndMessage.translateToSuccessMessage({
          bodyKey: manualClose
            ? 'CRM.MESSAGES.SUCCESS.MANUALLY_CLOSE'
            : 'CRM.MESSAGES.SUCCESS.UPDATE_MOC_EVENT',
          headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
        });
        this.determineEventStatus(this.mocEvent);
        this.updateForm();
        this.form?.disable();
        this.form?.markAsPristine();
        this.screenName = MOC_EVENT_TAB_PREFIX + this.mocEvent?.title;
        const oldTab = this._tabService.getActiveTab();
        const tab = this._tabService.buildNewTab(
          MOC_EVENT_DETAILS_CONTAINER,
          true,
          undefined,
          this.mocEvent?.id
        );
        this._tabService.replaceTab(oldTab, tab);
      },
    });
  }

  /**
   * Closes a MOC Event by updated the end date.
   */
  manuallyCloseMocEvent(): void {
    if (!this.canCreate) {
      return;
    }
    const endDt = new Date();
    endDt.setDate(endDt.getDate() - 1);
    this.form?.controls['endDt'].patchValue(endDt, { emitEvent: true });
    this.form?.controls['endDt'].markAsDirty();
    this.versionMocEvent(true);
  }

  updateForm(): void {
    let aors: any[] = [],
      categories: any[] = [],
      notes: any[] = [];
    this.mocEvent?.MOCEventAORs?.forEach((ed) => {
      aors = [...aors, ed.AOR?.id];
    });
    this.mocEvent?.MOCEventAssociatedCategories?.forEach((ec) => {
      categories = [...categories, ec.MOCEventCategory?.id];
    });
    this.mocEvent?.MOCEventEntries?.forEach((en) => {
      notes = [...notes, en];
    });
    this.form?.patchValue({
      title: this.mocEvent?.title,
      eventStatus: this.mocEvent?.eventStatus,
      eventType: this.mocEvent?.MOCEventType?.id,
      eventCategories: categories,
      aors: aors,
      notes: notes,
      startDt: this.mocEvent?.startDt,
      endDt: this.mocEvent?.endDt,
      document: this.mocEvent?.document,
      description: this.mocEvent?.description,
      createdBy: this.mocEvent?.createdBy,
      createdAt: this.mocEvent?.createdAt,
      sapOutageNumber: this.mocEvent?.sapOutageNumber,
      version: this.mocEvent?.version,
    });
  }

  /**
   * Load values for AOR's, Shift Groups, Event Types, and Event Categories.
   */
  loadDropDowns(): void {
    // Load AOR's
    const mocDistricts =
      this.mocEvent && this.mocEvent.MOCEventAORs
        ? this.mocEvent.MOCEventAORs
        : [];
    this._aorApi
      .getAORs(null)
      .pipe(take(1))
      .subscribe({
        next: (aors) => {
          this.districts = [
            ...aors
              .filter(
                (aor: AOR) =>
                  aor.enabled === true ||
                  !isNullOrUndefined(
                    mocDistricts.find((ex) => ex.AOR?.id === aor.id)
                  )
              )
              .sort((a: AOR, b: AOR) => sortByProperty(a, b, 'name'))
              .map((aor: AOR) => ({ label: aor.name, value: aor.id })),
          ];
        },
      });

    // Load Shift Groups
    this._shiftApi.getShiftGroups().subscribe((groups) => {
      this.shiftGroups = [
        { label: 'Choose', value: null },
        ...groups.map((sg) => ({
          label: sg.name,
          value: sg.id,
        })),
      ];
    });

    // Load MOCEventTypes
    this._mocApi.getMOCEventTypes().subscribe((types) => {
      this.eventTypes = [
        { label: 'Choose', value: null },
        ...types
          .filter(
            (et) =>
              et.enabled === true ||
              (this.mocEvent &&
                this.mocEvent.MOCEventType &&
                et.id === this.mocEvent.MOCEventType.id)
          )
          .map((et) => ({ label: et.name, value: et.id })),
      ];
      this.determineEventStatus(this.mocEvent);
    });

    // Load MOCEventCategories
    const mocCategories =
      this.mocEvent && this.mocEvent.MOCEventAssociatedCategories
        ? this.mocEvent.MOCEventAssociatedCategories
        : [];
    this._mocApi.getMOCEventCategories().subscribe((categoryConnection) => {
      this.eventCategories = [
        { label: 'Choose', value: null },
        ...categoryConnection.items
          .filter(
            (ec) =>
              ec.enabled === true ||
              (mocCategories &&
                !isNullOrUndefined(
                  mocCategories.find((ex) => ex.MOCEventCategory?.id === ec.id)
                ))
          )
          .map((ec) => ({ label: ec.name, value: ec.id })),
      ];
    });
  }

  /**
   * Check to determine if the form is valid.
   */
  isFormValid(): boolean {
    const eventTypeId = this.form?.controls['eventType'].value;
    const eventType = this.eventTypes.find((et) => et.value === eventTypeId);
    return (this.form?.valid &&
      eventType &&
      ((eventType.label === EVENT_TYPE_SCHEDULED &&
        !isNullOrUndefined(this.form.controls['endDt'].value)) ||
        eventType.label === EVENT_TYPE_PERMANENT))!;
  }

  /**
   * Deletes an MOC Event
   */
  deleteMOCEvent(): void {
    if (
      !this._authApi.doesUserHaveAllClaimsFromList([
        CLAIMS.CONTROL_ROOM.MOC_EVENTS.DELETE_MOC_EVENTS,
      ])
    ) {
      return;
    }

    if (!isNullOrUndefined(this.mocEvent)) {
      let confHeader = null,
        ok = null,
        cancel = null,
        message = null;
      const confHeader$ = this._translateService.get(
        'CRM.MESSAGES.HEADERS.DELETE_MOC_EVENT'
      );
      const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
      const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
      const message$ = this._translateService.get(
        'CRM.MESSAGES.CONFIRMATION.DELETE_MOC_EVENT'
      );

      forkJoin([confHeader$, ok$, cancel$, message$]).subscribe((messages) => {
        confHeader = messages[0];
        ok = messages[1];
        cancel = messages[2];
        message = messages[3];
      });

      this._confirmationService.confirm({
        header: confHeader!,
        icon: 'fa fa-question-circle',
        acceptVisible: true,
        rejectVisible: true,
        acceptLabel: ok!,
        rejectLabel: cancel!,
        message: message!,
        accept: () => {
          this.saving = true;
          if (this.mocEvent?.id) {
            // Only delete if existing
            this._mocApi.deleteMOCEvent(this.mocEvent.id).subscribe({
              next: (result) => {},
              error: (error) => {
                this.saving = false;
                this._logAndMessage.errorLogOnly(error);
                this._logAndMessage.translateToErrorAlert({
                  bodyKey: 'CRM.MESSAGES.ERROR.DELETE_MOC_EVENT',
                  headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
                });
              },
              complete: () => {
                this.saving = false;
                this._logAndMessage.translateToSuccessMessage({
                  bodyKey: 'CRM.MESSAGES.SUCCESS.DELETE_MOC_EVENT',
                  headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
                });
                const index = this._tabService.getActiveIndex();
                this._tabService.closeTab(index);
              },
            });
          }
        },
        reject: () => {},
      });
    }
  }

  updateAckForm(): void {
    if (this.selectedAck) {
      this.ackForm?.patchValue({
        controller: this.selectedAck.controller,
        comments: this.selectedAck.comments,
        completedDt: new Date(this.selectedAck.completedDt!),
        acknowledged: this.selectedAck.acknowledged,
      });
    }
  }

  acknowledgeMOCEvent(): void {
    this._contactApi
      .getContactByEmail(this._auth.getEmail()!)
      .pipe(take(1))
      .subscribe((data: Contact) => {
        const existingAck =
          this.mocEvent &&
          this.mocEvent.MOCEventAcknowledgements &&
          this.mocEvent.MOCEventAcknowledgements.find(
            (mea) => mea.Contact.id === data.id
          );
        if (!existingAck) {
          this.createMocEventAcknowledgement();
        } else if (existingAck && existingAck.acknowledged === false) {
          this.updateMocEventAcknowledgement(existingAck);
        } else {
          this._logAndMessage.translateToInfoMessage({
            bodyKey: 'CRM.MESSAGES.INFO.ALREADY_ACKNOWLEDGED',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          return;
        }
      });
  }

  createMocEventAcknowledgement(): void {
    this._contactApi
      .getContactByEmail(this._auth.getEmail()!)
      .pipe(take(1))
      .subscribe((data: Contact) => {
        const existingAck =
          this.mocEvent &&
          this.mocEvent.MOCEventAcknowledgements &&
          this.mocEvent.MOCEventAcknowledgements.find(
            (mea) => mea.Contact.id === data.id
          );
        if (existingAck && existingAck.acknowledged === true) {
          this._logAndMessage.translateToInfoMessage({
            bodyKey: 'CRM.MESSAGES.INFO.ALREADY_ACKNOWLEDGED',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
          return;
        }
        const ackInput: MOCEventAckInput =
          this._mocBuilder.buildMOCEventAcknowledgementInput(
            this.mocEvent!.id,
            data.id,
            existingAck!
          );
        this.saving = true;
        this._mocApi.createMOCEventAcknowledgement(ackInput).subscribe({
          next: (acknowledgement) => {
            this.mocEvent!.MOCEventAcknowledgements = [
              ...this.mocEvent!.MOCEventAcknowledgements,
              acknowledgement,
            ];
            this.acknowledgements = [
              ...this.mocEvent!.MOCEventAcknowledgements,
            ];
          },
          error: (error) => {
            this.saving = false;
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              bodyKey: 'CRM.MESSAGES.ERROR.ACKNOWLEDGE_MOC_EVENT',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
          complete: () => {
            this.saving = false;
            this._logAndMessage.translateToSuccessMessage({
              bodyKey: 'CRM.MESSAGES.SUCCESS.ACKNOWLEDGE_MOC_EVENT',
              headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
            });
          },
        });
      });
  }

  updateMocEventAcknowledgement(
    existingAck: MOCEventAcknowledgement | null = null
  ): void {
    const acknowledgement = existingAck ? existingAck : this.selectedAck!;
    acknowledgement.comments = existingAck
      ? existingAck.comments
      : this.ackForm?.controls['comments'].value;
    const changes: MOCEventAckInput =
      this._mocBuilder.buildMOCEventAcknowledgementInput(
        this.mocEvent!.id,
        acknowledgement.Contact.id,
        acknowledgement
      );
    this.saving = true;
    this._mocApi
      .updateMocEventAcknowledgement(acknowledgement.id, changes)
      .subscribe({
        next: (acknowledgement) => {
          const index = this.mocEvent!.MOCEventAcknowledgements.findIndex(
            (mea) => mea.id === acknowledgement.id
          );
          this.mocEvent!.MOCEventAcknowledgements[index] = acknowledgement;
          this.acknowledgements = [...this.mocEvent!.MOCEventAcknowledgements];
        },
        error: (error) => {
          this.saving = false;
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.ACKNOWLEDGE_MOC_EVENT',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          this.saving = false;
          this._logAndMessage.translateToSuccessMessage({
            bodyKey: 'CRM.MESSAGES.SUCCESS.ACKNOWLEDGE_MOC_EVENT',
            headerKey: 'COMMON.MESSAGES.HEADERS.SUCCESS',
          });
        },
      });
  }

  eventTypeChanged($event: SelectChangeEvent): void {
    if (!isNullOrUndefined($event) && !isNullOrUndefined($event.value)) {
      const type = this.eventTypes.find((met) => met.value === $event.value)!;
      if (!isNullOrUndefined(type) && type.label === EVENT_TYPE_SCHEDULED) {
        this.form?.controls['endDt'].enable();
      } else {
        this.form?.controls['endDt'].disable();
      }
    }
  }

  showEntryForm(): void {
    this.addEntryForm?.reset();
    this.showEntryDialog = true;
  }

  addMocEntry() {
    this.saving = true;
    const id = this.addEntryForm?.controls['id'].value;
    const note = this.addEntryForm?.controls['entry'].value;
    const existingEntry = this.mocEvent?.MOCEventEntries.find(
      (mee) => (mee.id = id)
    );
    let notes = [];
    if (existingEntry) {
      this.mocEvent!.MOCEventEntries.find((mee) => (mee.id = id))!.note = note;
      notes = [...this.mocEvent!.MOCEventEntries];
    } else {
      const mocEntry: MOCEventEntry = {
        id: id,
        note: note,
        user: '',
        time: new Date(),
      };
      notes = [...this.mocEvent!.MOCEventEntries, mocEntry];
    }
    this.form?.controls['notes'].patchValue(notes, { emitEvent: true });
    this.form?.controls['notes'].markAsDirty();
    this.versionMocEvent();
    this.showEntryDialog = false;
  }

  editEntry($event: any): void {
    this.addEntryForm?.patchValue({
      id: $event.id,
      entry: $event.note,
      dirtyStatus: DirtyStatus.UPDATED,
    });
    this.showEntryDialog = true;
  }

  deleteEntry($event: any): void {
    const deleteEntryMsg$ = this._translateService.get(
      'COMMON.MESSAGES.CONFIRMATION.DELETE_ENTRY'
    );
    const ok$ = this._translateService.get('COMMON.LABEL.BUTTONS.YES');
    const cancel$ = this._translateService.get('COMMON.LABEL.BUTTONS.NO');
    const deleteEntryHeader$ = this._translateService.get(
      'COMMON.MESSAGES.HEADERS.DELETE_ENTRY'
    );
    let okBtnLabel,
      cancelBtnLabel,
      deleteEntryMsg,
      deleteEntryHeader = null;
    forkJoin([ok$, cancel$, deleteEntryMsg$, deleteEntryHeader$]).subscribe(
      (messages) => {
        okBtnLabel = messages[0];
        cancelBtnLabel = messages[1];
        deleteEntryMsg = messages[2];
        deleteEntryHeader = messages[3];
      }
    );

    this._confirmationService.confirm({
      header: deleteEntryHeader!,
      icon: 'fa fa-question-circle',
      acceptVisible: true,
      rejectVisible: true,
      acceptLabel: okBtnLabel,
      rejectLabel: cancelBtnLabel,
      message: deleteEntryMsg,
      accept: () => {
        this.saving = true;
        const removeIndex = this.mocEvent!.MOCEventEntries.findIndex(
          (e) => e.id === $event.id
        );
        const notes = [...this.mocEvent!.MOCEventEntries];
        notes.splice(removeIndex, 1);
        this.form?.controls['notes'].patchValue(notes, { emitEvent: true });
        this.form?.controls['notes'].markAsDirty();
        this.versionMocEvent();
      },
      reject: () => {
        this.saving = false;
      },
    });
  }

  cancelEntryForm(): void {
    this.showEntryDialog = false;
  }

  setupActiveTabChangedSubscription(): void {
    this.activeTabChangedSub = this._tabService.activeTabChanged.subscribe(
      (tab) => {
        if (
          !isNullOrUndefined(tab) &&
          ((tab.header.startsWith('*') &&
            tab.header.includes(this.screenName)) ||
            (!tab.header.startsWith('*') && tab.header === this.screenName))
        ) {
          this.loadDropDowns();
          const dv: any = getDirtyValues(this.form);
          if (
            !isNullOrUndefined(dv) &&
            !isNullOrUndefined(dv.eventCategories)
          ) {
            const stillExist = verifyMOCEventCategoryStillValid(
              dv,
              this.eventCategories
            );
            if (!stillExist) {
              this.form?.controls['eventCategories'].patchValue(null, {
                emitEvent: true,
              });
              this._logAndMessage.translateToInfoMessage({
                bodyKey: 'CRM.MESSAGES.INFO.CATEGORY_DELETED_OR_DISABLED',
                headerKey: 'COMMON.MESSAGES.HEADERS.NOTIFICATION',
              });
            }
          }
        }
      }
    );
  }

  private viewHistoricalRecord(): void {
    const versions: SelectItem[] = Array.from(
      { length: this.mocEvent?.historyId ?? 0 },
      (_, version) => ({ label: 'V' + version, value: version })
    );
    this._dialogService
      .open(HistoryVersionDialogComponent, {
        header: this._translateService.instant(
          'COMMON.LABEL.VIEW_HISTORICAL_RECORD'
        ),
        modal: true,
        closable: true,
        dismissableMask: true,
        data: { versions },
      })
      ?.onClose.subscribe((version?: number) => {
        if (version !== undefined) {
          this.viewVersion(version);
        }
      });
  }

  viewVersion(selectedVersion: number): void {
    // Get MOC by historyId
    let mocEventId = null;
    if (selectedVersion > 0) {
      mocEventId = this.mocEvent!.parentMocId!;
      this._mocApi
        .getMOCEventByHistoryId(mocEventId, selectedVersion)
        .subscribe({
          next: (moc) => {
            if (!isNullOrUndefined(moc)) {
              this.historicalMocEvent = moc;
            }
          },
          error: (error) => {
            this._logAndMessage.errorLogOnly(error);
            this._logAndMessage.translateToErrorAlert({
              bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENT_VERSION',
              headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
            });
          },
          complete: () => {
            const newTitle =
              this.historicalMocEvent?.title +
              ' - V' +
              this.historicalMocEvent?.historyId;
            const tab = this._tabService.buildNewTab(
              MOC_EVENT_DETAILS_CONTAINER,
              true,
              newTitle,
              this.historicalMocEvent?.id
            );
            this._tabService.openTab(tab);
          },
        });
    } else {
      this._mocApi.getMOCEventById(this.mocEvent!.parentMocId!).subscribe({
        next: (mocEvent) => {
          if (!isNullOrUndefined(mocEvent)) {
            this.historicalMocEvent = mocEvent;
          }
        },
        error: (error) => {
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'CRM.MESSAGES.ERROR.GET_MOC_EVENT_VERSION',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
        complete: () => {
          const newTitle =
            this.historicalMocEvent?.title +
            ' - V' +
            this.historicalMocEvent?.historyId;
          const tab = this._tabService.buildNewTab(
            MOC_EVENT_DETAILS_CONTAINER,
            true,
            newTitle,
            this.historicalMocEvent?.id
          );
          this._tabService.openTab(tab);
        },
      });
    }
  }

  setDefaultDates(): void {
    this.defaultStartDate.setDate(this.defaultStartDate.getDate() + 1);
    this.defaultStartDate.setHours(0);
    this.defaultStartDate.setMinutes(0);
    this.defaultStartDate.setSeconds(0);
    this.defaultStartDate.setMilliseconds(0);

    this.defaultEndDate.setDate(this.defaultEndDate.getDate() + 2);
    this.defaultEndDate.setHours(0);
    this.defaultEndDate.setMinutes(0);
    this.defaultEndDate.setSeconds(0);
    this.defaultEndDate.setMilliseconds(0);
  }
}
